import React from "react";

import { RowAction } from "@tiny/components/src/ListItemRow/ListItemRow.types";
import { useTrafficShapingView } from "../TrafficShapingList.context";
import { TrafficShapingRowProps } from "./TrafficShapingRow.types";
import { getTimeActionsInfo } from "./TrafficShapingRow.helper";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";

export function useTrafficShapingRow(props: TrafficShapingRowProps) {
  const { ai_traffic_shaping_item } = props;

  const { mdUp } = useBreakpoints();

  const { isAITrafficShapingDeletable, selectionDisabled } =
    useTrafficShapingView();

  const {
    toggleSelectItem,
    isItemSelected,
    sortOption,
    onDeleteAITrafficShaping,
    onResetAITrafficShaping,
    selected,
    openAITrafficControlDetailsDialog,
    onRecurringToggle,
  } = useTrafficShapingView();

  const [detailsOpen, setDetailsOpen] = React.useState(false);

  const toggleDetails = React.useCallback(() => {
    setDetailsOpen((prev) => !prev);
  }, []);

  const [moreOpen, setMoreOpen] = React.useState(false);

  const [mutationLoading, setMutationLoading] = React.useState(false);

  const moreButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const showMore = React.useCallback(() => {
    setMoreOpen(true);
  }, []);

  const hideMore = React.useCallback(() => {
    setMoreOpen(false);
  }, []);

  const deletable = React.useMemo(
    () => isAITrafficShapingDeletable(ai_traffic_shaping_item),
    [ai_traffic_shaping_item, isAITrafficShapingDeletable]
  );

  const onSelect = React.useCallback(
    () => toggleSelectItem(ai_traffic_shaping_item),
    [ai_traffic_shaping_item, toggleSelectItem]
  );

  const isSelected = React.useMemo(
    () => () => isItemSelected(ai_traffic_shaping_item),
    [ai_traffic_shaping_item, isItemSelected]
  );

  const disabledCheckbox = React.useMemo(() => {
    if (selected?.length === 0) return selectionDisabled;
    const isThisSelected = isItemSelected(ai_traffic_shaping_item);
    return selectionDisabled || !isThisSelected;
  }, [selectionDisabled, selected, ai_traffic_shaping_item, isItemSelected]);

  const disabledCheckboxToolTip = React.useMemo(() => {
    return "Only One Item can be selected";
  }, []);

  const moreActions = React.useMemo(() => {
    const deleteAction = {
      name: "Delete",
      onClickAction: async () => {
        setMutationLoading(true);
        try {
          await onDeleteAITrafficShaping(ai_traffic_shaping_item);
        } finally {
          setMutationLoading(false);
        }
      },
    };
    const resetAction = {
      name: "Reset",
      onClickAction: async () => {
        setMutationLoading(true);
        try {
          await onResetAITrafficShaping(ai_traffic_shaping_item);
        } finally {
          setMutationLoading(false);
        }
      },
    };
    const detailsAction = {
      name: "View Segments",
      onClickAction: async () => {
        openAITrafficControlDetailsDialog(ai_traffic_shaping_item.id);
      },
    };

    const activateRecurring = {
      name: `${
        ai_traffic_shaping_item.recurring ? "Deactivate" : "Activate"
      } Recurring`,
      onClickAction: async () => {
        setMutationLoading(true);
        try {
          await onRecurringToggle(ai_traffic_shaping_item);
        } finally {
          setMutationLoading(false);
        }
      },
    };
    const rowActions: RowAction[] = [];
    if (deletable) {
      rowActions.push(deleteAction);
    }
    rowActions.push(resetAction);
    rowActions.push(detailsAction);
    rowActions.push(activateRecurring);

    return rowActions;
  }, [
    ai_traffic_shaping_item,
    deletable,
    onDeleteAITrafficShaping,
    onResetAITrafficShaping,
    openAITrafficControlDetailsDialog,
  ]);

  const checked = isSelected ? isSelected() : false;

  const timeActions = React.useMemo(
    () => getTimeActionsInfo(mdUp, ai_traffic_shaping_item, sortOption.action),
    [ai_traffic_shaping_item, mdUp, sortOption.action]
  );

  return {
    onSelect,
    disabledCheckbox,
    disabledCheckboxToolTip,
    sortAction: sortOption.action,
    moreActions,
    moreButtonRef,
    checked,
    mutationLoading,
    selectionDisabled,
    showMore,
    hideMore,
    moreOpen,
    detailsOpen,
    toggleDetails,
    timeActions,
  };
}
